import { InstantSearch } from "react-instantsearch-hooks";
import {
  Toggle,
  RefinementList,
  Hits,
  SearchableRefinementList,
  Pagination,
  SearchBox,
  CollapsibleFilter,
  FilterSection,
  searchStateToURL,
} from "@/components/SearchTools";
import { useEffect, useMemo, useState } from "react";
import PageInfo from "@/components/PageInfo";
// import { shuffle } from "@/utils/random";
// import { DATABASE_SIZE, PINNED_PROFILE } from "@/utils/profile";
import { DATABASE_SIZE } from "@/utils/profile";
import searchClient, { interceptSearchRequests } from "@/utils/algolia-init";
import {
  Container,
  chakra,
  ButtonGroup,
  Box,
  Flex,
  Grid,
  Spacer,
  Text,
  Icon,
} from "@chakra-ui/react";
import Navbar from "@/components/Navbar";
import { EditIcon } from "@chakra-ui/icons";
import Footer from "@/components/Footer";
import { useRouter } from "next/router";
import { useUserData, withUserData } from "@/contexts/UserDataContext";
import { joinWithCommaAnd } from "@/utils/string";
import { LinkButton } from "@/components/shared/generic";
import { FaFilter } from "react-icons/fa";
import LargeLoadingSection from "@/components/LargeLoadingSection";
import { find } from "@/utils/data/colleges-data";
import { ProfileTable } from "@/components/ProfileTable";
import ModalFreeUnlockEligible from "@/components/plans/ModalFreeUnlockEligible";
import ModalFindYourTwin from "@/components/profiles/ModalFindYourTwin";
import { createCheckeredBackground } from "@/utils/style";
import Head from "next/head";
import CollegeAdvisorModal from "@/components/collegeadvisor/CollegeAdvisorModal";
import { COMPLETENESS_TOTAL_SCORE } from "@/utils/completeness/completenessScore";

const MIN_COMPLETENESS_SCORE = Math.floor(COMPLETENESS_TOTAL_SCORE / 2);

// const FREE_UNLOCK_ENABLED = false;
// const RANDOM_SEED = Math.floor(Math.random() * 100000);
export const PROFILES_PAGE_GRADIENT = "linear-gradient(to top left, #ff6565 10%, #1d2473 90%)";

// function transformSearchResults(data) {
//   for (const queryResult of data.results) {
//     const { hits } = queryResult;
//     shuffle(hits, RANDOM_SEED, 0, hits.length);
//
//     const indexOfPinned = hits.findIndex(
//       (hit) => hit.objectID === PINNED_PROFILE,
//     );
//     if (indexOfPinned !== -1) {
//       // Swap the order -- move pinned profile to 1st
//       const placeholder = hits[0];
//       hits[0] = hits[indexOfPinned];
//       hits[indexOfPinned] = placeholder;
//     }
//   }
// }

const Hero = () => {
  return (
    <>
      <Navbar
        customLogo={
          <Flex direction="column" mt={2}>
            <Text fontSize="3xl" fontWeight={600} lineHeight="16px">
              admityogi
            </Text>

            <Text color="#EF38AA" fontWeight={600}>
              Profiles
            </Text>
          </Flex>
        }
      />

      <Container textAlign="center" my={20}>
        <chakra.h1 textStyle="pageHeader" fontSize={{ base: "6xl", md: "8xl" }}>
          <chakra.span bg={PROFILES_PAGE_GRADIENT} bgClip="text">
            Get in
          </chakra.span>
          <chakra.span bg="linear-gradient(#888, #333)" bgClip="text">
            spired.
          </chakra.span>
        </chakra.h1>
        <chakra.h2 fontFamily="heading" color="gray.700" fontSize={{ base: "2xl", md: "4xl" }}>
          Thousands of verified applications. Take your pick.
        </chakra.h2>
      </Container>
    </>
  );
};

const FilterArea = () => (
  <Grid
    templateColumns={{
      base: "1fr",
      md: "repeat(3, 1fr)",
      lg: "repeat(4, 1fr)",
    }}
    overflow="visible"
    columnGap={4}
    rowGap={4}
    pl={{ base: 2, md: 0 }}
    mt={4}
  >
    {/* Make the box take up all of the row */}
    <Box gridColumn="1/-1" mt={{ base: 4, md: 0 }}>
      <CollapsibleFilter w="100%" borderColor="gray.200" header="📝 Full-text search">
        <SearchBox placeholder="You can type an activity (e.g., debate/nonprofit/research), essay content (e.g., volunteering), or an award (e.g., ISEF)" />
        <Toggle
          as="check"
          title="Only show profiles eligible for a free profile unlock"
          attribute="eligible_for_free"
        />
      </CollapsibleFilter>
    </Box>
    <CollapsibleFilter header="🎓 Dream School" color="purple.700">
      <SearchableRefinementList
        sorted="college"
        title="Select a university..."
        attribute="accepted_colleges"
        limit={1000}
      />
    </CollapsibleFilter>
    <CollapsibleFilter header="🧬 Major Interests" color="red.700">
      <SearchableRefinementList
        sorted="count"
        title="Search for a major..."
        attribute="academic_interests"
        limit={100}
      />
    </CollapsibleFilter>
    <CollapsibleFilter header="📚 Stats & Tests" color="purple.700">
      <ButtonGroup isAttached>
        <Toggle title="Test-optional applicant" attribute="is_test_optional" />
        <Toggle title="Not test optional" attribute="is_not_test_optional" />
      </ButtonGroup>
      {/* <RangeSelector title="SAT score" attribute="sat_superscore" min={400} max={1600} precision={50} /> */}
      {/* <RangeSelector title="ACT score" attribute="act_superscore" min={0} max={36} precision={1} /> */}
    </CollapsibleFilter>
    <CollapsibleFilter header="🏫 High School Type" color="red.700">
      <SearchableRefinementList margin attribute="highschool_type" />
    </CollapsibleFilter>
    <CollapsibleFilter header="📍 Geography" color="red.700">
      <ButtonGroup isAttached>
        <Toggle title="Domestic" attribute="is_domestic" />
        <Toggle title="International" attribute="is_international" />
      </ButtonGroup>
      <div style={{ minHeight: "0.5em" }} />
      <SearchableRefinementList
        sorted="count"
        title="Filter by country..."
        attribute="origin_country"
        limit={50}
      />
      <SearchableRefinementList
        sorted="count"
        title="Filter by state..."
        attribute="origin_state"
        limit={50}
      />
    </CollapsibleFilter>
    <CollapsibleFilter header="👦🏾 Background" color="purple.700">
      <ButtonGroup isAttached>
        <Toggle title="Legacy" attribute="is_legacy" />
        <Toggle title="NOT legacy" attribute="is_not_legacy" />
      </ButtonGroup>
      <Toggle margin title="First-generation" attribute="is_first_gen" />
      <Toggle margin title="Recruited athlete" attribute="is_athlete" />
      <Toggle margin title="Low-income" attribute="is_low_income" />
    </CollapsibleFilter>
    <CollapsibleFilter header="👫 Gender" color="red.700">
      <ButtonGroup isAttached>
        <RefinementList limit={3} attribute="sex" />
      </ButtonGroup>
    </CollapsibleFilter>
    <CollapsibleFilter header="🌍 Race" color="purple.700">
      <RefinementList limit={4} margin attribute="ethnicity" />
    </CollapsibleFilter>
  </Grid>
);

const AdvancedSearch = ({ loading = false }) => {
  const onStateChange = ({ uiState, setUiState }) => {
    window.history.pushState(uiState, "", searchStateToURL(uiState));
    setUiState(uiState);
  };

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName="admityogi_users"
      onStateChange={onStateChange}
    >
      {loading && (
        <LargeLoadingSection
          minH={10}
          pb="50vh"
          text={`Preparing our database of ${DATABASE_SIZE.applications} applications for you...`}
          fontSize={{ base: "md", md: "xl" }}
        />
      )}
      <Box visibility={loading ? "hidden" : "shown"}>
        <Container maxW="container.xl" id="results" my={8}>
          <FilterSection>
            <FilterArea />
          </FilterSection>
        </Container>

        <Container maxW="container.xl" my={20}>
          <Pagination />
          <Hits />
        </Container>
      </Box>
      =
    </InstantSearch>
  );
};

export const SimpleSearch = ({ maxNProfiles = 20, maxW = "container.xl", showButtons = true }) => {
  const { userData } = useUserData();

  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState();

  const infoCuration = useMemo(() => {
    let newInfoCuration = "";
    const collegeCount = userData.targetColleges?.length ?? 0;
    if (collegeCount > 0) {
      newInfoCuration +=
        " at " +
        joinWithCommaAnd(
          userData.targetColleges.slice(0, 3).map((formalName) => find(formalName)?.name),
          "or",
        );
    }
    const academicInterestCount = userData.academicInterests?.length ?? 0;
    if (academicInterestCount > 0) {
      newInfoCuration +=
        " from students studying " + joinWithCommaAnd(userData.academicInterests, "or");
    }
    if (userData.satScore) {
      newInfoCuration += " with an SAT score of " + userData.satScore;
    }
    if (userData.actScore) {
      newInfoCuration += " with an ACT score of " + userData.actScore;
    }
    if (newInfoCuration.length === 0) {
      newInfoCuration = " from our dataset of " + DATABASE_SIZE.applications + ".";
    } else {
      newInfoCuration += ".";
    }
    return "Curating the best applications from" + newInfoCuration;
  }, [userData]);

  const loadRecommendedProfiles = async () => {
    const optionalFilters = [];

    for (const college of userData.targetColleges ?? []) {
      optionalFilters.push(`accepted_colleges:${college}`);
    }

    for (const college of userData.academicInterests ?? []) {
      optionalFilters.push(`academic_interests:${college}`);
    }

    const index = searchClient.initIndex("admityogi_users");
    const result = await index.search("", {
      optionalFilters,
      hitsPerPage: maxNProfiles,
      filters: `show_in_search_results:true AND completeness_score >= ${MIN_COMPLETENESS_SCORE}`,
    });

    setProfiles(result.hits);
    setLoading(false);
  };

  useEffect(() => {
    loadRecommendedProfiles();
  }, []);

  return (
    <>
      <Box pb={10}>
        {showButtons && (
          <>
            <Container maxW="container.md" textAlign="center">
              <Text fontSize={{ base: "md", md: "xl" }}>{infoCuration}</Text>
            </Container>
          </>
        )}
        <Container maxW={maxW} mt={10}>
          {showButtons && (
            <Flex>
              <Spacer />
              <ButtonGroup mb={10} isAttached>
                <LinkButton
                  href="?advanced=true"
                  leftIcon={<Icon as={FaFilter} />}
                  colorScheme="black"
                >
                  Add more filters
                </LinkButton>
                <LinkButton
                  href="/get-started/hs?askAdditionalQuestions=true"
                  leftIcon={<EditIcon />}
                  variant="outline"
                  colorScheme="black"
                >
                  Edit my information
                </LinkButton>
              </ButtonGroup>
              <Spacer />
            </Flex>
          )}
          {loading ? (
            <LargeLoadingSection
              minH="50vh"
              text={`Searching ${DATABASE_SIZE.applications} college applications to find your twin...`}
            />
          ) : (
            <ProfileTable profiles={profiles} />
          )}
        </Container>
      </Box>
    </>
  );
};

function Profiles() {
  const router = useRouter();
  const { currentUser, refreshUserData } = useUserData();
  const [freeUnlockEligible, setFreeUnlockEligible] = useState(false);
  const [showAddPropertiesModal, setShowAddPropertiesModal] = useState(false);

  const [selectedSearch, setSelectedSearch] = useState(undefined); // simple | advanced
  const [isAdvancedSearchLoading, setIsAdvancedSearchLoading] = useState(true);

  const bg = createCheckeredBackground("1em", "#000000", "05");

  // const testFreeUnlock = async () => {
  //   const { eligible } = await api.get("users/free-credits", true);
  //   setFreeUnlockEligible(eligible);
  // };

  // ADVANCED SEARCH INTERCEPT
  useEffect(() => {
    interceptSearchRequests(async (requests) => {
      for (const req of requests) {
        if (Array.isArray(req.params.facets)) {
          req.params.facets = req.params.facets.filter((e) => e !== "show_in_search_results");
          req.params.facetFilters =
            req.params.facetFilters?.filter((e) => !e[0].startsWith("show_in_search_results")) ??
            [];

          req.params.facets.push("show_in_search_results");
          req.params.facetFilters.push([
            "show_in_search_results:true",
            `completeness_score >= ${MIN_COMPLETENESS_SCORE}`,
          ]);
        } else {
          req.params.facetFilters = [
            ["show_in_search_results:true", `completeness_score >= ${MIN_COMPLETENESS_SCORE}`],
          ];
        }
      }
    });

    const searchCompletedHandler = () => {
      setIsAdvancedSearchLoading(false);
    };

    window.addEventListener("algoliaSearchCompleted", searchCompletedHandler);

    return () => {
      window.removeEventListener("algoliaSearchCompleted", searchCompletedHandler);
    };
  }, []);

  useEffect(() => {
    const handleSearchSelection = async () => {
      if (router.query.advanced === "true") {
        setSelectedSearch("advanced");
        return;
      }

      if (currentUser === undefined) return; // Loading

      if (currentUser === null) {
        setSelectedSearch("advanced");
        setShowAddPropertiesModal(true);
        return;
      }

      const userData = await refreshUserData();

      if (!userData || userData.type !== "hs") {
        setSelectedSearch("advanced");
        return;
      }

      const hasTargetColleges = userData.targetColleges?.length > 0;
      const hasAcademicInterests = userData.academicInterests?.length > 0;

      if (hasTargetColleges || hasAcademicInterests) {
        setSelectedSearch("simple");
      } else {
        setShowAddPropertiesModal(true);
        setSelectedSearch("advanced");
      }
    };

    handleSearchSelection();
  }, [currentUser, router.query]);

  return (
    <>
      <PageInfo
        title="Search Profiles"
        description="Use our 20+ custom filters to find admits just like you."
        themeColor="#ffffff"
      />
      <Head>
        <link rel="canonical" href={process.env.NEXT_PUBLIC_DOMAIN_SHARE + "/profiles"} />
      </Head>
      <CollegeAdvisorModal />
      <Box {...bg}>
        <Hero />
        <ModalFreeUnlockEligible
          isOpen={freeUnlockEligible}
          onClose={() => setFreeUnlockEligible(false)}
        />
        {!freeUnlockEligible && showAddPropertiesModal && (
          <ModalFindYourTwin onClose={() => setShowAddPropertiesModal(false)} />
        )}

        <Box pos="relative" minHeight="40vh">
          {selectedSearch === undefined && (
            <LargeLoadingSection
              minH={10}
              pb="50vh"
              text={`Preparing our database of ${DATABASE_SIZE.applications} applications for you...`}
              fontSize={{ base: "md", md: "xl" }}
            />
          )}
          {selectedSearch === "simple" && <SimpleSearch />}
          {selectedSearch === "advanced" && <AdvancedSearch loading={isAdvancedSearchLoading} />}
        </Box>
      </Box>
      <Footer />
    </>
  );
}

export default withUserData(Profiles);
